import { createFileRoute, notFound } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useLayoutEffect } from "react";
import { z } from "zod";

import type { ResumeData } from "@/schema/resume/data";
import type { Template } from "@/schema/templates";

import { ResumePreview } from "@/components/resume/preview";
import { useResumeStore } from "@/components/resume/store/resume";
import { sampleResumeData } from "@/schema/resume/sample";

const searchSchema = z.object({
  columns: z.enum(["single", "multi"]).catch("multi"),
  mode: z.enum(["auto", "manual"]).catch("auto"),
  scenario: z
    .enum(["normal", "sidebar-overflow", "single-item-overflow", "long-text-overflow", "manual-regression"])
    .catch("normal"),
  template: z.enum(["onyx", "azurill"]).catch("onyx"),
});

type Search = z.infer<typeof searchSchema>;

const duplicateItems = <T extends { id: string }>(items: T[], count: number): T[] =>
  Array.from({ length: count }, (_, index) => ({
    ...items[index % items.length],
    id: `${items[index % items.length].id}-${index}`,
  }));

const buildArtifactResumeData = ({ columns, scenario, template }: Search): ResumeData => {
  const data = structuredClone(sampleResumeData) as ResumeData;

  data.picture.hidden = true;
  data.metadata.template = template as Template;
  data.metadata.page.marginX = 18;
  data.metadata.page.marginY = 16;
  data.metadata.layout.pages = [
    {
      fullWidth: false,
      main: ["summary", "experience", "019becaf-0b87-769d-98a6-46ccf558c0e8", "projects", "education"],
      sidebar: ["profiles", "skills", "languages", "certifications"],
    },
  ];

  data.summary.columns = columns === "multi" ? 2 : 1;
  data.sections.skills.columns = columns === "multi" ? 3 : 1;
  data.sections.languages.columns = columns === "multi" ? 2 : 1;

  if (scenario === "sidebar-overflow") {
    data.sections.skills.items = duplicateItems(data.sections.skills.items, 24);
    data.sections.languages.items = duplicateItems(data.sections.languages.items, 12);
    data.sections.certifications.items = duplicateItems(data.sections.certifications.items, 10);
  }

  if (scenario === "single-item-overflow") {
    const [firstProject] = data.sections.projects.items;
    if (firstProject) {
      data.sections.projects.items = [
        {
          ...firstProject,
          id: "single-item-overflow-project",
          description: `<ul>${Array.from(
            { length: 36 },
            (_, index) => `<li><p>Oversized project detail ${index + 1} with enough copy to exceed one page.</p></li>`,
          ).join("")}</ul>`,
        },
      ];
    }
  }

  if (scenario === "long-text-overflow") {
    data.summary.columns = 1;
    data.summary.content = Array.from(
      { length: 36 },
      (_, index) => `<p>Long summary paragraph ${index + 1}: this paragraph intentionally stays indivisible in P0.</p>`,
    ).join("");
  }

  return data;
};

export const Route = createFileRoute("/dev/auto-pagination-artifacts")({
  component: RouteComponent,
  validateSearch: zodValidator(searchSchema),
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  loaderDeps: ({ search }) => search,
  loader: ({ deps }) => {
    const data = buildArtifactResumeData(deps);
    const mode = deps.scenario === "manual-regression" ? "manual" : deps.mode;

    return {
      resume: {
        id: `auto-pagination-artifact-${deps.template}-${deps.scenario}`,
        name: "Auto Pagination Artifact Resume",
        slug: "auto-pagination-artifact-resume",
        tags: [],
        isLocked: false,
        data,
        paginationPreference: { mode, modeSource: "user" as const, template: deps.template },
      },
    };
  },
});

function RouteComponent() {
  const { resume } = Route.useLoaderData();
  const isReady = useResumeStore((state) => state.isReady);
  const initialize = useResumeStore((state) => state.initialize);

  useLayoutEffect(() => {
    initialize(resume);
    return () => initialize(null);
  }, [initialize, resume]);

  if (!isReady) return <div className="p-8 text-sm text-muted-foreground">Loading auto pagination artifact...</div>;

  return (
    <main className="min-h-screen bg-zinc-100 p-8 print:bg-white print:p-0">
      <ResumePreview showPageNumbers className="flex items-start gap-10 print:block" pageClassName="print:w-full!" />
    </main>
  );
}
